import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Plus, Pencil, Trash2, CalendarDays, ShoppingBag, Users, DollarSign, Mail, ArrowUp, ArrowDown, ClipboardList, Eye, Settings2, CheckCircle2, XCircle, Bell, Heart, Target, QrCode, Send, Sparkles, Copy, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import ImageUpload from "@/components/admin/ImageUpload";
import { api, formatApiError } from "@/lib/api";

const EMPTY_EVENT = { title: "", description: "", seo_description: "", date_label: "", start_date: "", start_time: "", end_time: "", location: "", price: 0, capacity: 50, image: "", category: "Community & Culture", event_type: "Seminar or Talk", ticketed: false, form_id: "", variations: [], featured: false, active: true, registration_opens: "", registration_closes: "" };
const EMPTY_PRODUCT = { name: "", description: "", seo_description: "", price: 25, image: "", category: "Merch", sizes: [], variations: [], requires_shipping: true, featured: false, active: true };
const EMPTY_FORM = { name: "", purpose: "general", fields: [], submit_label: "Submit", success_message: "Thank you! Your submission was received.", active: true };

const FIELD_TYPES = [
  ["text", "Short text"], ["email", "Email"], ["phone", "Phone"], ["number", "Number"],
  ["textarea", "Long text"], ["select", "Dropdown"], ["checkbox", "Checkbox"], ["date", "Date"],
];

const PURPOSES = [
  ["general", "General"], ["contact", "Contact"], ["event_registration", "Event Registration"],
];

const IMAGE_PRESETS = ["/assets/academy-1.png", "/assets/academy-2.png", "/assets/product-academy.png", "/assets/product-book.png", "/assets/product-shirt-unlocking.jpg", "/assets/product-shirt-aspire.jpg", "/assets/product-shirt-bet.jpg", "/assets/logo.png"];

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "border-gold/20 bg-ink";
const selectCls = `h-10 w-full rounded-md border ${inputCls} px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/50`;

// Shared SEO description field with an AI writer — used by event, product and page forms.
function SeoField({ value, onChange, kind, title, body, testId }) {
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!String(title || "").trim()) {
      toast.error("Add a title first so the AI has some context.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/seo/generate", { kind, title, body: body || "" });
      onChange(data.description);
      toast.success("SEO description written — tweak it however you like.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Field label="SEO description (optional — the snippet Google shows)">
      <Textarea data-testid={`${testId}-seo-input`} rows={2} maxLength={160} value={value || ""}
        onChange={(e) => onChange(e.target.value)} className={inputCls}
        placeholder="Leave blank and Google will auto-use the first lines of the description" />
      <div className="mt-1.5 flex items-center justify-between">
        <p data-testid={`${testId}-seo-count`} className="text-[11px] text-muted-foreground">{(value || "").length}/160</p>
        <button type="button" data-testid={`${testId}-seo-ai-button`} onClick={generate} disabled={generating}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold transition-colors hover:text-gold-hover disabled:opacity-50">
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Write with AI
        </button>
      </div>
    </Field>
  );
}

function EventForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || EMPTY_EVENT);
  const [forms, setForms] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/admin/forms").then(({ data }) => setForms(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        capacity: Number(form.capacity),
        variations: (form.variations || []).filter((v) => String(v.name).trim()).map((v) => ({ ...v, price: Number(v.price) || 0 })),
      };
      if (form.id) await api.put(`/admin/events/${form.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(form.id ? "Event updated." : "Event created.");
      onSave();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="event-form" onSubmit={submit} className="space-y-4">
      <Field label="Title"><Input data-testid="event-title-input" required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></Field>
      <Field label="Description"><Textarea data-testid="event-description-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
      <SeoField testId="event" kind="event" title={form.title} body={form.description} value={form.seo_description} onChange={(v) => set("seo_description", v)} />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Date label (e.g. July 10–12, 2026)"><Input data-testid="event-date-label-input" value={form.date_label} onChange={(e) => set("date_label", e.target.value)} className={inputCls} /></Field>
        <Field label="Start date"><Input data-testid="event-start-date-input" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Start time (optional)"><Input data-testid="event-start-time-input" type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className={inputCls} /></Field>
        <Field label="End time (optional)"><Input data-testid="event-end-time-input" type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className={inputCls} /></Field>
      </div>
      <div className="rounded-xl border border-gold/10 bg-ink/60 p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Registration window (optional — leave blank to always allow registration)</p>
        <div className="mt-3 grid grid-cols-2 gap-4">
          <Field label="Registration opens"><Input data-testid="event-reg-opens-input" type="date" value={form.registration_opens || ""} onChange={(e) => set("registration_opens", e.target.value)} className={inputCls} /></Field>
          <Field label="Registration closes"><Input data-testid="event-reg-closes-input" type="date" value={form.registration_closes || ""} onChange={(e) => set("registration_closes", e.target.value)} className={inputCls} /></Field>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">Outside this window visitors see a notice instead of enroll buttons — and before opening day they can leave their email to be notified automatically.</p>
      </div>
      <Field label="Location"><Input data-testid="event-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} /></Field>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Price ($0 = free)"><Input data-testid="event-price-input" type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} /></Field>
        <Field label="Capacity"><Input data-testid="event-capacity-input" type="number" min="1" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className={inputCls} /></Field>
        <Field label="Category"><Input data-testid="event-category-input" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} /></Field>
      </div>
      <Field label="Registration form (free events)">
        <select data-testid="event-form-select" value={form.form_id || ""} onChange={(e) => set("form_id", e.target.value)} className={selectCls}>
          <option value="">Default registration form</option>
          {forms.map((f) => <option key={f.id} value={f.id}>{f.name}{f.purpose === "event_registration" ? "" : ` (${f.purpose})`}</option>)}
        </select>
        <p className="mt-1.5 text-[11px] text-muted-foreground">Free events use this form as their ticketing signup. Paid events go through the shop checkout.</p>
      </Field>
      <Field label="Ticket tiers (paid events — optional)">
        <div className="space-y-3">
          {(form.variations || []).map((v, i) => (
            <div key={i} data-testid={`event-tier-row-${i}`} className="rounded-xl border border-gold/15 bg-ink p-3.5">
              <div className="grid grid-cols-[1fr_110px] gap-3">
                <Input data-testid={`event-tier-name-${i}`} placeholder='Tier name (e.g. "General Admission")' value={v.name}
                  onChange={(e) => set("variations", form.variations.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))} className={inputCls} />
                <Input data-testid={`event-tier-price-${i}`} type="number" min="0" step="0.01" placeholder="Price" value={v.price}
                  onChange={(e) => set("variations", form.variations.map((x, idx) => (idx === i ? { ...x, price: e.target.value } : x)))} className={inputCls} />
              </div>
              <div className="mt-2.5 flex items-center gap-3">
                <Input data-testid={`event-tier-desc-${i}`} placeholder="Short description (optional)" value={v.description}
                  onChange={(e) => set("variations", form.variations.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))} className={inputCls} />
                <button type="button" data-testid={`event-tier-delete-${i}`} aria-label="Remove tier"
                  onClick={() => set("variations", form.variations.filter((_, idx) => idx !== i))}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
          <Button type="button" data-testid="event-add-tier-button" size="sm" variant="outline"
            onClick={() => set("variations", [...(form.variations || []), { id: "", name: "", price: "", description: "" }])}
            className="border-gold/30 bg-transparent text-gold hover:bg-gold/10">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add Ticket Tier
          </Button>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Tiers let one event offer multiple prices (e.g. Free workshop, $35 adult combo, $120 VIP group). Buyers pick a tier per attendee on the event page — each attendee gets their own QR ticket. With tiers set, the base price above is only a fallback.
          </p>
        </div>
      </Field>
      <Field label="Image URL or /assets path">
        <Input data-testid="event-image-input" value={form.image} onChange={(e) => set("image", e.target.value)} className={inputCls} placeholder="/assets/academy-1.png" />
        <div className="mt-2">
          <ImageUpload value={form.image} onUploaded={(url) => set("image", url)} testId="event-image-upload" />
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {IMAGE_PRESETS.map((p) => (
            <button type="button" key={p} data-testid={`event-image-preset-${p.split("/").pop()}`} onClick={() => set("image", p)}
              className={`rounded border px-2 py-1 font-mono text-[10px] transition-colors ${form.image === p ? "border-gold text-gold" : "border-gold/15 text-muted-foreground hover:border-gold/40"}`}>
              {p.split("/").pop()}
            </button>
          ))}
        </div>
      </Field>
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        <label className="flex items-center gap-2 text-sm"><input data-testid="event-ticketed-checkbox" type="checkbox" checked={form.ticketed} onChange={(e) => set("ticketed", e.target.checked)} className="accent-[#D4AF37]" /> Sell tickets through checkout</label>
        <label className="flex items-center gap-2 text-sm"><input data-testid="event-featured-checkbox" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#D4AF37]" /> Featured</label>
        <label className="flex items-center gap-2 text-sm"><input data-testid="event-active-checkbox" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#D4AF37]" /> Visible on site</label>
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        "Sell tickets through checkout" lets buyers enroll right from the event page — set a price above $0 (or add ticket tiers) and buyers check out with attendee names and QR tickets. Events never appear in the shop; the shop is merch only. Leave it off for free events with a registration form.
      </p>
      <Button data-testid="event-save-button" type="submit" disabled={saving} className="w-full rounded-full bg-gold py-5 font-semibold text-ink hover:bg-gold-hover">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form.id ? "Save Changes" : "Create Event"}
      </Button>
    </form>
  );
}

function ProductForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(
    initial
      ? { ...initial, sizesText: (initial.sizes || []).join(", "), requires_shipping: initial.requires_shipping ?? !(initial.variations || []).length }
      : { ...EMPTY_PRODUCT, sizesText: "" }
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { sizesText, ...rest } = form;
      const payload = {
        ...rest,
        price: Number(form.price),
        sizes: sizesText.split(",").map((s) => s.trim()).filter(Boolean),
        variations: (form.variations || []).filter((v) => String(v.name).trim()).map((v) => ({ ...v, price: Number(v.price) || 0 })),
      };
      if (form.id) await api.put(`/admin/products/${form.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(form.id ? "Product updated." : "Product created.");
      onSave();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="product-form" onSubmit={submit} className="space-y-4">
      <Field label="Name"><Input data-testid="product-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Field>
      <Field label="Description"><Textarea data-testid="product-description-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
      <SeoField testId="product" kind="product" title={form.name} body={form.description} value={form.seo_description} onChange={(v) => set("seo_description", v)} />
      <div className="grid grid-cols-3 gap-4">
        <Field label="Price (USD)"><Input data-testid="product-price-input" type="number" min="0" step="0.01" required value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} /></Field>
        <Field label="Category"><Input data-testid="product-category-input" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} /></Field>
        <Field label="Sizes (comma separated)"><Input data-testid="product-sizes-input" value={form.sizesText} onChange={(e) => set("sizesText", e.target.value)} className={inputCls} placeholder="S, M, L, XL" /></Field>
      </div>
      <Field label="Image URL or /assets path">
        <Input data-testid="product-image-input" value={form.image} onChange={(e) => set("image", e.target.value)} className={inputCls} placeholder="/assets/product-book.png" />
        <div className="mt-2">
          <ImageUpload value={form.image} onUploaded={(url) => set("image", url)} testId="product-image-upload" />
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {IMAGE_PRESETS.map((p) => (
            <button type="button" key={p} data-testid={`product-image-preset-${p.split("/").pop()}`} onClick={() => set("image", p)}
              className={`rounded border px-2 py-1 font-mono text-[10px] transition-colors ${form.image === p ? "border-gold text-gold" : "border-gold/15 text-muted-foreground hover:border-gold/40"}`}>
              {p.split("/").pop()}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Ticket options / variations (optional)">
        <div className="space-y-3">
          {(form.variations || []).map((v, i) => (
            <div key={i} data-testid={`variation-row-${i}`} className="rounded-xl border border-gold/15 bg-ink p-3.5">
              <div className="grid grid-cols-[1fr_110px] gap-3">
                <Input data-testid={`variation-name-${i}`} placeholder='Option name (e.g. "Workshop Only")' value={v.name}
                  onChange={(e) => set("variations", form.variations.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))} className={inputCls} />
                <Input data-testid={`variation-price-${i}`} type="number" min="0" step="0.01" placeholder="Price" value={v.price}
                  onChange={(e) => set("variations", form.variations.map((x, idx) => (idx === i ? { ...x, price: e.target.value } : x)))} className={inputCls} />
              </div>
              <div className="mt-2.5 flex items-center gap-3">
                <Input data-testid={`variation-desc-${i}`} placeholder="Short description (optional)" value={v.description}
                  onChange={(e) => set("variations", form.variations.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))} className={inputCls} />
                <button type="button" data-testid={`variation-delete-${i}`} aria-label="Remove variation"
                  onClick={() => set("variations", form.variations.filter((_, idx) => idx !== i))}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
          <Button type="button" data-testid="add-variation-button" size="sm" variant="outline"
            onClick={() => set("variations", [...(form.variations || []), { id: "", name: "", price: "", description: "" }])}
            className="border-gold/30 bg-transparent text-gold hover:bg-gold/10">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add Variation
          </Button>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            With variations, buyers choose a quantity of each option in one order — e.g. 1 Adult ($35) + 1 Child (Free). Set price 0 for free options. Sizes are ignored when variations exist.
          </p>
        </div>
      </Field>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm"><input data-testid="product-featured-checkbox" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#D4AF37]" /> Featured</label>
        <label className="flex items-center gap-2 text-sm"><input data-testid="product-active-checkbox" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#D4AF37]" /> Visible in shop</label>
        <label className="flex items-center gap-2 text-sm" title="Ask for a mailing address at checkout">
          <input data-testid="product-shipping-checkbox" type="checkbox" checked={!!form.requires_shipping} onChange={(e) => set("requires_shipping", e.target.checked)} className="accent-[#D4AF37]" /> Requires shipping (mailed item)
        </label>
      </div>
      <Button data-testid="product-save-button" type="submit" disabled={saving} className="w-full rounded-full bg-gold py-5 font-semibold text-ink hover:bg-gold-hover">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form.id ? "Save Changes" : "Create Product"}
      </Button>
    </form>
  );
}

function FormBuilder({ initial, onSave, onClose }) {
  const [form, setForm] = useState(
    initial
      ? { ...initial, fields: (initial.fields || []).map((f) => ({ ...f, optionsText: (f.options || []).join(", ") })) }
      : { ...EMPTY_FORM, fields: [] }
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addField = () =>
    set("fields", [...form.fields, { id: "", label: "", type: "text", required: false, optionsText: "", placeholder: "" }]);
  const updateField = (i, k, v) =>
    set("fields", form.fields.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  const removeField = (i) => set("fields", form.fields.filter((_, idx) => idx !== i));
  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.fields.length) return;
    const next = [...form.fields];
    [next[i], next[j]] = [next[j], next[i]];
    set("fields", next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.fields.length === 0) {
      toast.error("Add at least one field.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        fields: form.fields.map(({ optionsText, ...f }) => ({
          ...f,
          options: (optionsText || "").split(",").map((s) => s.trim()).filter(Boolean),
        })),
      };
      delete payload.id;
      delete payload.created_at;
      if (form.id) await api.put(`/admin/forms/${form.id}`, payload);
      else await api.post("/admin/forms", payload);
      toast.success(form.id ? "Form updated." : "Form created.");
      onSave();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="form-builder" onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Form name"><Input data-testid="form-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. Volunteer Signup" /></Field>
        <Field label="Purpose">
          <select data-testid="form-purpose-select" value={form.purpose} onChange={(e) => set("purpose", e.target.value)} className={selectCls}>
            {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Submit button label"><Input data-testid="form-submit-label-input" value={form.submit_label} onChange={(e) => set("submit_label", e.target.value)} className={inputCls} /></Field>
        <Field label="Success message"><Input data-testid="form-success-message-input" value={form.success_message} onChange={(e) => set("success_message", e.target.value)} className={inputCls} /></Field>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Fields ({form.fields.length})</Label>
          <Button type="button" data-testid="form-add-field-button" size="sm" variant="outline" onClick={addField}
            className="border-gold/30 bg-transparent text-gold hover:bg-gold/10">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add Field
          </Button>
        </div>
        <div className="mt-3 space-y-3">
          {form.fields.map((f, i) => (
            <div key={i} data-testid={`form-field-row-${i}`} className="rounded-xl border border-gold/15 bg-ink p-4">
              <div className="grid grid-cols-[1fr_130px] gap-3">
                <Input data-testid={`field-label-${i}`} placeholder="Field label" required value={f.label}
                  onChange={(e) => updateField(i, "label", e.target.value)} className={inputCls} />
                <select data-testid={`field-type-${i}`} value={f.type} onChange={(e) => updateField(i, "type", e.target.value)} className={selectCls}>
                  {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Input data-testid={`field-placeholder-${i}`} placeholder="Placeholder (optional)" value={f.placeholder}
                  onChange={(e) => updateField(i, "placeholder", e.target.value)} className={inputCls} />
                {f.type === "select" ? (
                  <Input data-testid={`field-options-${i}`} placeholder="Options, comma separated" value={f.optionsText}
                    onChange={(e) => updateField(i, "optionsText", e.target.value)} className={inputCls} />
                ) : (
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input data-testid={`field-required-${i}`} type="checkbox" checked={f.required}
                      onChange={(e) => updateField(i, "required", e.target.checked)} className="accent-[#D4AF37]" />
                    Required
                  </label>
                )}
              </div>
              {f.type === "select" && (
                <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                  <input data-testid={`field-required-${i}`} type="checkbox" checked={f.required}
                    onChange={(e) => updateField(i, "required", e.target.checked)} className="accent-[#D4AF37]" />
                  Required
                </label>
              )}
              <div className="mt-3 flex justify-end gap-1.5">
                <button type="button" data-testid={`field-up-${i}`} aria-label="Move up" onClick={() => moveField(i, -1)}
                  className="flex h-7 w-7 items-center justify-center rounded-full border border-gold/20 text-muted-foreground hover:border-gold/50 hover:text-gold"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" data-testid={`field-down-${i}`} aria-label="Move down" onClick={() => moveField(i, 1)}
                  className="flex h-7 w-7 items-center justify-center rounded-full border border-gold/20 text-muted-foreground hover:border-gold/50 hover:text-gold"><ArrowDown className="h-3.5 w-3.5" /></button>
                <button type="button" data-testid={`field-delete-${i}`} aria-label="Remove field" onClick={() => removeField(i)}
                  className="flex h-7 w-7 items-center justify-center rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
          {form.fields.length === 0 && (
            <p className="rounded-xl border border-dashed border-gold/20 py-8 text-center text-sm text-muted-foreground">
              No fields yet — click "Add Field" to build your form.
            </p>
          )}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm"><input data-testid="form-active-checkbox" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#D4AF37]" /> Active (usable on the site)</label>
      <Button data-testid="form-save-button" type="submit" disabled={saving} className="w-full rounded-full bg-gold py-5 font-semibold text-ink hover:bg-gold-hover">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form.id ? "Save Changes" : "Create Form"}
      </Button>
    </form>
  );
}

function SubmissionsDialog({ form, onClose }) {
  const [subs, setSubs] = useState(null);

  useEffect(() => {
    if (!form) return;
    api.get(`/admin/submissions?form_id=${form.id}`)
      .then(({ data }) => setSubs(data))
      .catch((e) => { toast.error(formatApiError(e)); setSubs([]); });
  }, [form]);

  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="submissions-dialog" className="max-h-[85vh] overflow-y-auto border-gold/25 bg-ink-card text-foreground sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-xl">Submissions — {form?.name}</DialogTitle></DialogHeader>
        {subs === null ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : subs.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No submissions yet.</p>
        ) : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} data-testid={`submission-row-${s.id}`} className="rounded-xl border border-gold/15 bg-ink p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</p>
                  {s.event_id && <span className="rounded-full border border-gold/25 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-gold">event signup</span>}
                </div>
                <dl className="mt-3 space-y-1.5">
                  {Object.entries(s.data || {}).map(([k, v]) => (
                    <div key={k} className="flex gap-3 text-sm">
                      <dt className="w-40 shrink-0 text-muted-foreground">{k}</dt>
                      <dd className="min-w-0 flex-1 break-words">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentSettings() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ mode: "test", publishable_key: "", secret_key: "", webhook_secret: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/settings/payments")
      .then(({ data }) => {
        setCfg(data);
        setForm((f) => ({ ...f, mode: data.mode, publishable_key: data.publishable_key || "", secret_key: "", webhook_secret: "" }));
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setTestResult(null);
    try {
      await api.put("/admin/settings/payments", form);
      toast.success("Payment settings saved.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/settings/payments/test");
      setTestResult({ ok: true, text: `Connected — account ${data.account_id}${data.email ? ` (${data.email})` : ""}, charges ${data.charges_enabled ? "enabled" : "NOT enabled yet"}` });
    } catch (err) {
      setTestResult({ ok: false, text: formatApiError(err) });
    } finally {
      setTesting(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Remove the custom Stripe account and go back to the platform default?")) return;
    try {
      await api.delete("/admin/settings/payments");
      toast.success("Reverted to the default payment account.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  const webhookUrl = `${window.location.origin}/api/stripe/webhook`;

  return (
    <div className="max-w-3xl space-y-6" data-testid="payment-settings">
      {cfg.onboarding_url && (
        <div
          data-testid="stripe-connect-card"
          className={`rounded-2xl border p-6 ${
            cfg.account_status?.charges_enabled
              ? "border-emerald-400/40 bg-emerald-500/10"
              : "border-gold/40 bg-gold/5"
          }`}
        >
          {cfg.account_status?.charges_enabled ? (
            <div className="flex items-start gap-4">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-300" />
              <div>
                <h3 className="font-display text-lg font-semibold text-emerald-200">Stripe account connected</h3>
                <p className="mt-1 text-xs leading-relaxed text-emerald-200/70">
                  Your account is verified and ready to accept real payments{cfg.account_status.email ? ` — ${cfg.account_status.email}` : ""}.
                  {cfg.mode !== "live" ? " Switch the mode below to Live when you're ready to charge real cards." : ""}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="max-w-md">
                <h3 className="font-display text-lg font-semibold">Connect your Stripe account</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Sign in on Stripe's secure page — the platform links everything automatically and securely. No keys to copy, nothing to paste.
                  {!cfg.account_status?.details_submitted ? " Right now the site runs on a shared test account until you claim yours." : " Your details are in — Stripe is reviewing your account; charges switch on once approved."}
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button asChild data-testid="stripe-connect-button"
                  className="rounded-full bg-gold px-6 font-semibold text-ink hover:bg-gold-hover">
                  <a href={cfg.onboarding_url} target="_blank" rel="noopener noreferrer">Connect with Stripe</a>
                </Button>
                <Button data-testid="stripe-refresh-status-button" variant="outline" onClick={load}
                  className="rounded-full border-gold/30 bg-transparent text-gold hover:bg-gold/10">
                  I've connected — refresh
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="rounded-2xl border border-gold/20 bg-ink-card/70 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Settings2 className="h-5 w-5 text-gold" />
            <h3 className="font-display text-lg font-semibold">Stripe — payment processor</h3>
          </div>
          <span className={`rounded-full border px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] ${
            cfg.mode === "live" ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-amber-400/40 bg-amber-400/10 text-amber-300"
          }`} data-testid="payment-mode-pill">
            {cfg.mode === "live" ? "Live — real charges" : "Test mode"}
          </span>
        </div>
        <dl className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Account source</dt>
            <dd className="mt-1">{cfg.source === "custom" ? "Custom (set in admin)" : "Platform default (sandbox)"}</dd></div>
          <div><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Secret key</dt>
            <dd className="mt-1 font-mono text-xs">{cfg.secret_key_last4 ? `••••••${cfg.secret_key_last4}` : "—"}</dd></div>
          <div><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Publishable key</dt>
            <dd className="mt-1 truncate font-mono text-xs">{cfg.publishable_key || "—"}</dd></div>
          <div><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Webhook</dt>
            <dd className="mt-1">{cfg.webhook_configured ? "Configured" : "Not configured"}</dd></div>
        </dl>
        <div className="mt-5 rounded-xl border border-gold/15 bg-ink p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Webhook endpoint — add this in your Stripe Dashboard → Developers → Webhooks</p>
          <p className="mt-1.5 break-all font-mono text-xs text-gold" data-testid="webhook-url">{webhookUrl}</p>
          <p className="mt-2 text-xs text-muted-foreground">Listen for: payment_intent.succeeded, payment_intent.payment_failed, charge.refunded. Paste the webhook signing secret below.</p>
        </div>
      </div>

      <form onSubmit={save} className="rounded-2xl border border-gold/20 bg-ink-card/70 p-6" data-testid="payment-settings-form">
        <h3 className="font-display text-lg font-semibold">Advanced — paste keys manually</h3>
        <p className="mt-1 text-xs text-muted-foreground">Only needed for a custom Stripe setup. The Connect button above is the recommended way to link your account. Leave a key field blank to keep the current value.</p>
        <div className="mt-5 space-y-4">
          <Field label="Mode">
            <div className="flex gap-3" role="radiogroup" aria-label="Payment mode">
              {[["test", "Test — no real charges"], ["live", "Live — GO LIVE"]].map(([v, l]) => (
                <button type="button" key={v} data-testid={`payment-mode-${v}`} onClick={() => setForm({ ...form, mode: v })}
                  aria-pressed={form.mode === v}
                  className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                    form.mode === v
                      ? v === "live" ? "border-emerald-400 bg-emerald-400/10 text-emerald-300" : "border-gold bg-gold/10 text-gold"
                      : "border-gold/15 text-muted-foreground hover:border-gold/40"
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Publishable key (pk_test_… / pk_live_…)">
            <Input data-testid="publishable-key-input" value={form.publishable_key} onChange={(e) => setForm({ ...form, publishable_key: e.target.value })}
              className={`${inputCls} font-mono text-xs`} placeholder="pk_live_…" />
          </Field>
          <Field label="Secret key (sk_test_… / sk_live_…)">
            <Input data-testid="secret-key-input" type="password" value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
              className={`${inputCls} font-mono text-xs`} placeholder={cfg.secret_key_last4 ? `••••••${cfg.secret_key_last4} (leave blank to keep)` : "sk_live_…"} autoComplete="new-password" />
          </Field>
          <Field label="Webhook signing secret (whsec_…) — optional">
            <Input data-testid="webhook-secret-input" type="password" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
              className={`${inputCls} font-mono text-xs`} placeholder="whsec_… (leave blank to keep)" autoComplete="new-password" />
          </Field>
        </div>
        {form.mode === "live" && (
          <p className="mt-4 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-4 py-2.5 text-xs leading-relaxed text-emerald-300" data-testid="live-warning">
            Going live: real cards will be charged. Make sure your Stripe account is fully activated, and re-register the webhook above in the live account.
          </p>
        )}
        {testResult && (
          <p data-testid="connection-test-result" className={`mt-4 flex items-start gap-2 rounded-lg border px-4 py-2.5 text-xs ${testResult.ok ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
            {testResult.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />} {testResult.text}
          </p>
        )}
        <div className="mt-5 flex flex-wrap gap-3">
          <Button data-testid="payment-save-button" type="submit" disabled={saving} className="rounded-full bg-gold px-6 font-semibold text-ink hover:bg-gold-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
          </Button>
          <Button data-testid="payment-test-button" type="button" variant="outline" onClick={testConnection} disabled={testing}
            className="rounded-full border-gold/40 bg-transparent text-gold hover:bg-gold/10">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test Connection"}
          </Button>
          {cfg.source === "custom" && (
            <Button data-testid="payment-reset-button" type="button" variant="outline" onClick={reset}
              className="rounded-full border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10">
              Reset to Default
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

const CONTENT_FIELDS = [
  ["hero_quote", "Hero quote (new line = line break)", true],
  ["hero_title_1", "Hero title — line 1", false],
  ["hero_title_2", "Hero title — line 2", false],
  ["hero_subcopy", "Hero paragraph", true],
  ["bet_strip", "Bottom strip (B.E.T line)", false],
  ["contact_heading", "Contact section heading", false],
  ["contact_subcopy", "Contact section paragraph", true],
  ["contact_phone", "Contact phone", false],
  ["contact_email", "Contact email", false],
  ["donate_heading", "Donate section heading", false],
  ["donate_subcopy", "Donate section paragraph", true],
];

function ContentManager() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setValues(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { values });
      toast.success("Site content updated — live immediately.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!values) return <div className="flex py-10"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  return (
    <div className="max-w-3xl" data-testid="content-manager">
      <div className="space-y-4 rounded-2xl border border-gold/20 bg-ink-card/70 p-6">
        {CONTENT_FIELDS.map(([key, label, long]) => (
          <Field key={key} label={label}>
            {long ? (
              <Textarea data-testid={`content-${key}`} rows={3} value={values[key] || ""}
                onChange={(e) => setValues({ ...values, [key]: e.target.value })} className={inputCls} />
            ) : (
              <Input data-testid={`content-${key}`} value={values[key] || ""}
                onChange={(e) => setValues({ ...values, [key]: e.target.value })} className={inputCls} />
            )}
          </Field>
        ))}
        <Button data-testid="content-save-button" onClick={save} disabled={saving}
          className="rounded-full bg-gold px-8 font-semibold text-ink hover:bg-gold-hover">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish Changes"}
        </Button>
      </div>
    </div>
  );
}

const EMPTY_PAGE = { title: "", slug: "", body: "", image: "", seo_description: "", active: true };

function PagesManager() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/pages").then(({ data }) => setPages(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/pages/${editing.id}`, editing);
      else await api.post("/admin/pages", editing);
      toast.success(editing.id ? "Page updated." : "Page created — it's live in the menu.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this page?")) return;
    await api.delete(`/admin/pages/${id}`).then(() => { toast.success("Page deleted."); load(); }).catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="max-w-3xl" data-testid="pages-manager">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Custom pages appear in the site menu automatically and live at /p/your-slug.</p>
        <Button data-testid="add-page-button" size="sm" onClick={() => setEditing({ ...EMPTY_PAGE })} className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
          <Plus className="mr-1.5 h-4 w-4" /> Add Page
        </Button>
      </div>
      <div className="space-y-3">
        {pages.map((p) => (
          <div key={p.id} data-testid={`admin-page-row-${p.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-gold/15 bg-ink-card/60 p-4">
            <div className="min-w-0 flex-1">
              <p className="truncate font-display font-semibold">{p.title}</p>
              <p className="font-mono text-xs text-muted-foreground">/p/{p.slug} · {p.active ? "Live" : "Hidden"}</p>
            </div>
            <div className="flex gap-2">
              <Button data-testid={`admin-page-view-${p.id}`} size="sm" variant="outline" asChild className="border-gold/30 bg-transparent text-gold hover:bg-gold/10">
                <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer"><Eye className="h-3.5 w-3.5" /></a>
              </Button>
              <Button data-testid={`admin-page-edit-${p.id}`} size="sm" variant="outline" onClick={() => setEditing(p)} className="border-gold/30 bg-transparent text-gold hover:bg-gold/10"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button data-testid={`admin-page-delete-${p.id}`} size="sm" variant="outline" onClick={() => remove(p.id)} className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {pages.length === 0 && <p className="rounded-xl border border-dashed border-gold/20 py-8 text-center text-sm text-muted-foreground">No custom pages yet — or ask the AI Editor below to create one.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="page-form-dialog" className="max-h-[90vh] overflow-y-auto border-gold/25 bg-ink-card text-foreground sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing?.id ? "Edit Page" : "Add Page"}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4" data-testid="page-form">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Title"><Input data-testid="page-title-input" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} /></Field>
                <Field label="Slug (optional — auto from title)"><Input data-testid="page-slug-input" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className={inputCls} placeholder="our-story" /></Field>
              </div>
              <Field label="Body (blank line between paragraphs)">
                <Textarea data-testid="page-body-input" rows={10} required value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className={inputCls} />
              </Field>
              <SeoField testId="page" kind="page" title={editing.title} body={editing.body} value={editing.seo_description} onChange={(v) => setEditing({ ...editing, seo_description: v })} />
              <Field label="Header image (optional)">
                <Input data-testid="page-image-input" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} className={inputCls} placeholder="/assets/academy-1.png" />
                <div className="mt-2"><ImageUpload value={editing.image} onUploaded={(url) => setEditing({ ...editing, image: url })} testId="page-image-upload" /></div>
              </Field>
              <label className="flex items-center gap-2 text-sm"><input data-testid="page-active-checkbox" type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-[#D4AF37]" /> Live on site</label>
              <Button data-testid="page-save-button" type="submit" disabled={saving} className="w-full rounded-full bg-gold py-5 font-semibold text-ink hover:bg-gold-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing.id ? "Save Changes" : "Create Page"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMPTY_POST = { title: "", slug: "", body: "", image: "", seo_description: "", published: false };

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/blog").then(({ data }) => setPosts(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/blog/${editing.id}`, editing);
      else await api.post("/admin/blog", editing);
      toast.success(editing.id ? "Post updated." : editing.published ? "Post published — it's live on /blog." : "Draft saved.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete the post "${p.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/blog/${p.id}`).then(() => { toast.success("Post deleted."); load(); }).catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div data-testid="blog-manager">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Blog posts show at /blog — drafts stay hidden until you publish them.</p>
        <Button data-testid="new-post-button" onClick={() => setEditing({ ...EMPTY_POST })}
          className="rounded-full bg-gold px-5 font-semibold text-ink hover:bg-gold-hover">
          <Plus className="mr-1.5 h-4 w-4" /> New Post
        </Button>
      </div>
      {posts.length === 0 ? (
        <div className="rounded-2xl border border-gold/15 bg-ink-card/50 p-10 text-center text-sm text-muted-foreground" data-testid="blog-empty-state">
          No posts yet — write your first one, or ask the AI Editor to draft it for you.
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} data-testid={`blog-row-${p.slug}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-gold/10 bg-ink-card/50 p-4">
              <div className="min-w-0 flex-1">
                <p className="font-display font-semibold leading-snug">{p.title}</p>
                <p className="mt-0.5 font-mono text-xs text-muted-foreground">/blog/{p.slug}</p>
              </div>
              <span data-testid={`blog-status-${p.slug}`} className={`rounded-full border px-2.5 py-0.5 text-xs ${p.published ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-amber-400/30 bg-amber-400/10 text-amber-300"}`}>
                {p.published ? "Published" : "Draft"}
              </span>
              <Button data-testid={`blog-edit-${p.slug}`} size="sm" variant="outline" onClick={() => setEditing({ ...p })}
                className="border-gold/30 bg-transparent text-gold hover:bg-gold/10"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button data-testid={`blog-delete-${p.slug}`} size="sm" variant="outline" onClick={() => del(p)}
                className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-gold/20 bg-ink text-foreground">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Edit Post" : "New Post"}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4" data-testid="blog-form">
              <Field label="Title"><Input data-testid="blog-title-input" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} /></Field>
              <Field label="URL slug (optional — auto from title)">
                <Input data-testid="blog-slug-input" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className={inputCls} placeholder="my-first-post" />
              </Field>
              <Field label="Featured image (optional)">
                <ImageUpload value={editing.image} onUploaded={(url) => setEditing({ ...editing, image: url })} testId="blog-image-upload" />
              </Field>
              <Field label="Body (blank line between paragraphs)">
                <Textarea data-testid="blog-body-input" rows={10} required value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className={inputCls} />
              </Field>
              <SeoField testId="blog" kind="blog post" title={editing.title} body={editing.body} value={editing.seo_description} onChange={(v) => setEditing({ ...editing, seo_description: v })} />
              <label className="flex items-center gap-3 text-sm">
                <Switch data-testid="blog-published-switch" checked={!!editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} /> Published (visible on the site)
              </label>
              <Button data-testid="blog-save-button" type="submit" disabled={saving}
                className="w-full rounded-full bg-gold py-5 font-semibold text-ink hover:bg-gold-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing.id ? "Save Changes" : editing.published ? "Publish Post" : "Save Draft"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AiEditor() {
  const [instruction, setInstruction] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    api.get("/admin/ai-edits").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const run = async (e) => {
    e.preventDefault();
    if (!instruction.trim()) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/ai-edit", { instruction });
      setResult(data);
      setInstruction("");
      loadHistory();
      if (data.applied.length) toast.success("AI changes applied — live on the site now.");
      else toast.info("No changes applied — see the summary.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="ai-editor">
      <form onSubmit={run} className="rounded-2xl border border-gold/25 bg-ink-card/70 p-6">
        <h3 className="font-display text-lg font-semibold">Describe the change — AI applies it</h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Examples: "Change the hero quote to …" · "Add a page about our scholarship program" · "Add a $35 parenting workshop on August 14" · "Create a gold logo tee for $25 and publish it". The AI can edit site text, manage custom pages, and create new events and products — saved as hidden drafts unless you say "publish" or "make it live". Design or code changes (colors, layout) still go through Sitelytic.
        </p>
        <Textarea
          data-testid="ai-instruction-input"
          rows={4}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="e.g. Change the hero quote to 'Empowering Leaders. Transforming Futures.' and update the contact phone to 225-555-0100"
          className={`${inputCls} mt-4`}
        />
        <Button data-testid="ai-apply-button" type="submit" disabled={running || !instruction.trim()}
          className="mt-4 rounded-full bg-gold px-8 font-semibold text-ink hover:bg-gold-hover">
          {running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> AI is working…</> : "Apply with AI"}
        </Button>
      </form>

      {result && (
        <div className="rounded-2xl border border-gold/20 bg-ink-card/70 p-6" data-testid="ai-result">
          <p className="font-display font-semibold">{result.summary}</p>
          {result.applied.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {result.applied.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-emerald-300"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {a}</li>
              ))}
            </ul>
          )}
          {result.skipped.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {result.skipped.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-amber-300"><XCircle className="mt-0.5 h-4 w-4 shrink-0" /> {s}</li>
              ))}
            </ul>
          )}
          <a href="/" target="_blank" rel="noopener noreferrer" data-testid="ai-view-site-link" className="mt-4 inline-block text-xs text-gold hover:underline">View the live site →</a>
        </div>
      )}

      {history.length > 0 && (
        <div className="rounded-2xl border border-gold/15 bg-ink-card/60 p-6" data-testid="ai-history">
          <h3 className="font-display text-lg font-semibold">Recent AI edits</h3>
          <div className="mt-4 space-y-3">
            {history.slice(0, 8).map((h) => (
              <div key={h.id} className="border-b border-gold/10 pb-3 last:border-0">
                <p className="text-sm text-foreground/85">"{h.instruction}"</p>
                <p className="mt-1 text-xs text-muted-foreground">{h.summary} · {new Date(h.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_CAMPAIGN = { title: "", goal: "", description: "", end_date: "", active: false };

function TicketsDialog({ reg, onClose }) {
  const [sending, setSending] = useState(false);

  const resend = async () => {
    setSending(true);
    try {
      await api.post(`/admin/registrations/${reg.id}/resend-ticket`);
      toast.success(`QR tickets emailed to ${reg.email}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!reg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tickets-dialog" className="max-h-[90vh] overflow-y-auto border-gold/25 bg-ink-card text-foreground sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-xl">QR Tickets — {reg?.name}</DialogTitle></DialogHeader>
        {reg && (
          <>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {reg.event_title} · each attendee shows their code at the door for instant check-in.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {(reg.tickets || []).map((t, i) => (
                <div key={t.code} data-testid={`ticket-cell-${i}`} className="rounded-2xl border border-gold/15 bg-ink p-3.5 text-center">
                  <img src={`/api/tickets/${t.code}/qr.png`} alt={`QR code for ${t.name || "guest"}`} className="mx-auto h-24 w-24 rounded-lg bg-white p-1.5" />
                  <p className="mt-2 truncate text-xs font-semibold">{t.name || "Guest"}</p>
                  <p className={`mt-0.5 text-[10px] font-medium ${t.checked_in ? "text-emerald-300" : "text-muted-foreground"}`}>
                    {t.checked_in ? `Checked in${t.checked_in_at ? ` · ${new Date(t.checked_in_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}` : "Not checked in"}
                  </p>
                </div>
              ))}
              {(!reg.tickets || reg.tickets.length === 0) && (
                <p className="col-span-full py-6 text-center text-sm text-muted-foreground">No tickets yet — resend the email to generate them.</p>
              )}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button data-testid="resend-ticket-button" size="sm" onClick={resend} disabled={sending || !reg.email}
                className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-3.5 w-3.5" /> Resend ticket email</>}
              </Button>
              {reg.tickets?.[0] && (
                <a href={`/ticket/${reg.tickets[0].code}`} target="_blank" rel="noopener noreferrer" data-testid="ticket-page-link"
                  className="text-xs text-gold hover:underline">Open ticket page →</a>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StaffManager() {
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/staff").then(({ data }) => setStaff(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/staff", form);
      toast.success(`Check-in login created for ${form.email} — share the /checkin link and these credentials.`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove check-in access for ${email}?`)) return;
    try {
      await api.delete(`/admin/staff/${email}`);
      toast.success("Staff login removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!staff) return <div className="flex py-6"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-4" data-testid="staff-manager">
      <p className="max-w-xl text-xs leading-relaxed text-muted-foreground">
        Door staff sign in at <span className="font-mono text-gold">/checkin</span> on any phone or tablet — they pick the event and scan attendee QR codes. Staff logins can't access anything else.
      </p>
      <form onSubmit={create} className="grid grid-cols-1 gap-3 rounded-2xl border border-gold/20 bg-ink-card/70 p-5 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <Input data-testid="staff-name-input" required placeholder="Name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        <Input data-testid="staff-email-input" required type="email" placeholder="Email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <Input data-testid="staff-password-input" required type="password" minLength="8" placeholder="Password (min 8 chars)" value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} autoComplete="new-password" />
        <Button data-testid="staff-create-button" type="submit" disabled={saving} className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add</>}
        </Button>
      </form>
      <div className="space-y-2">
        {staff.map((s) => (
          <div key={s.email} data-testid={`staff-row-${s.email}`} className="flex items-center gap-4 rounded-xl border border-gold/15 bg-ink-card/60 p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/20 bg-ink text-gold">
              <QrCode className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{s.name}</p>
              <p className="truncate text-xs text-muted-foreground">{s.email}</p>
            </div>
            <Button data-testid={`staff-delete-${s.email}`} aria-label={`Remove check-in access for ${s.email}`} size="sm" variant="outline" onClick={() => remove(s.email)}
              className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        {staff.length === 0 && <p className="rounded-xl border border-dashed border-gold/20 py-6 text-center text-sm text-muted-foreground">No extra staff logins — the default check-in account works too.</p>}
      </div>
    </div>
  );
}

function CampaignsManager() {
  const [campaigns, setCampaigns] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/campaigns").then(({ data }) => setCampaigns(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title: form.title, goal: parseFloat(form.goal) || 0, description: form.description || "", end_date: form.end_date || "", active: !!form.active };
      if (form.id) await api.put(`/admin/campaigns/${form.id}`, payload);
      else await api.post("/admin/campaigns", payload);
      toast.success(form.id ? "Campaign updated." : "Campaign created — switch it on to show it on the Donate section.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    try {
      await api.put(`/admin/campaigns/${c.id}`, { title: c.title, goal: c.goal, description: c.description || "", end_date: c.end_date || "", active: !c.active });
      toast.success(!c.active ? "Campaign is live on the Donate section." : "Campaign hidden from the site.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the "${c.title}" campaign? Past donations are kept, but progress tracking stops.`)) return;
    try {
      await api.delete(`/admin/campaigns/${c.id}`);
      toast.success("Campaign deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!campaigns) return <div className="flex py-6"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-4" data-testid="campaigns-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-xs leading-relaxed text-muted-foreground">
          Goal-tracked fundraisers (e.g. "$5,000 for the 2026 Academy") with a live progress bar on the Donate section. A campaign only appears on the site while its switch is on.
        </p>
        <Button data-testid="campaign-create-button" size="sm" onClick={() => setForm({ ...EMPTY_CAMPAIGN })} className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
          <Plus className="mr-1.5 h-4 w-4" /> New Campaign
        </Button>
      </div>

      {form && (
        <form onSubmit={save} className="rounded-2xl border border-gold/25 bg-ink-card/70 p-5" data-testid="campaign-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>Campaign title</Label>
              <Input data-testid="campaign-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. $5,000 for the 2026 Academy" className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <Label>Goal amount ($)</Label>
              <Input data-testid="campaign-goal-input" required type="number" min="1" step="1" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })}
                placeholder="5000" className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <Label>End date (optional)</Label>
              <Input data-testid="campaign-end-date-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input data-testid="campaign-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What this campaign funds" className={`${inputCls} mt-1.5`} />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Switch data-testid="campaign-active-switch" checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <span className="text-xs text-muted-foreground">Show on the Donate section now</span>
          </div>
          <div className="mt-4 flex gap-3">
            <Button data-testid="campaign-save-button" type="submit" disabled={saving} className="rounded-full bg-gold px-6 font-semibold text-ink hover:bg-gold-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form.id ? "Save Changes" : "Create Campaign"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setForm(null)} className="rounded-full border-gold/30 bg-transparent text-gold hover:bg-gold/10">Cancel</Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {campaigns.map((c) => (
          <div key={c.id} data-testid={`campaign-row-${c.id}`} className="rounded-xl border border-gold/15 bg-ink-card/60 p-4">
            <div className="flex flex-wrap items-center gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gold/20 bg-ink text-gold">
                <Target className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display font-semibold">{c.title}</p>
                <p className="text-xs text-muted-foreground">
                  ${c.raised.toLocaleString()} of ${Number(c.goal).toLocaleString()} · {c.percent}%{c.end_date ? ` · ends ${c.end_date}` : ""} · {c.active ? "Live on site" : "Hidden"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid={`campaign-toggle-${c.id}`} checked={c.active} onCheckedChange={() => toggle(c)} />
                <Button data-testid={`campaign-edit-${c.id}`} size="sm" variant="outline" onClick={() => setForm({ ...c, goal: String(c.goal) })} className="border-gold/30 bg-transparent text-gold hover:bg-gold/10"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button data-testid={`campaign-delete-${c.id}`} size="sm" variant="outline" onClick={() => remove(c)} className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gold/10">
              <div className="h-full rounded-full bg-gold transition-[width] duration-700" style={{ width: `${Math.max(c.raised > 0 ? 2 : 0, c.percent)}%` }} />
            </div>
          </div>
        ))}
        {campaigns.length === 0 && <p className="rounded-xl border border-dashed border-gold/20 py-8 text-center text-sm text-muted-foreground">No campaigns yet — create one when you're running a goal-based fundraiser.</p>}
      </div>
    </div>
  );
}

const EMPTY_PROMO = { code: "", type: "percent", value: "", applies_to: "all", active: true };

function PromosManager() {
  const [promos, setPromos] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/promos").then(({ data }) => setPromos(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, value: parseFloat(form.value) || 0 };
      if (form.id) await api.put(`/admin/promos/${form.id}`, payload);
      else await api.post("/admin/promos", payload);
      toast.success(form.id ? "Promo code updated." : "Promo code created.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo code "${p.code}"? It will stop working immediately.`)) return;
    try {
      await api.delete(`/admin/promos/${p.id}`);
      toast.success("Promo code deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!promos) return <div className="flex py-6"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-4" data-testid="promos-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-xs leading-relaxed text-muted-foreground">
          Discount codes for early-bird tickets or merch sales. Shoppers enter them at checkout — turn a code off anytime to stop it instantly.
        </p>
        <Button data-testid="promo-create-button" size="sm" onClick={() => setForm({ ...EMPTY_PROMO })} className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
          <Plus className="mr-1.5 h-4 w-4" /> New Promo Code
        </Button>
      </div>

      {form && (
        <form onSubmit={save} className="rounded-2xl border border-gold/25 bg-ink-card/70 p-5" data-testid="promo-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>Code</Label>
              <Input data-testid="promo-code-input" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="EARLYBIRD20" className={`${inputCls} mt-1.5 uppercase`} />
            </div>
            <div>
              <Label>Discount</Label>
              <div className="mt-1.5 flex gap-2">
                <select data-testid="promo-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="rounded-lg border border-gold/20 bg-ink px-3 py-2 text-sm text-foreground focus:border-gold focus:outline-none">
                  <option value="percent">% off</option>
                  <option value="fixed">$ off</option>
                </select>
                <Input data-testid="promo-value-input" required type="number" min="0.01" step="0.01" value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === "percent" ? "20" : "10.00"} className={inputCls} />
              </div>
            </div>
            <div>
              <Label>Applies to</Label>
              <select data-testid="promo-applies-select" value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-gold/20 bg-ink px-3 py-2 text-sm text-foreground focus:border-gold focus:outline-none">
                <option value="all">Everything (merch + events)</option>
                <option value="products">Merchandise only</option>
                <option value="events">Event tickets only</option>
              </select>
            </div>
            <div className="flex items-end gap-3 pb-1">
              <Switch data-testid="promo-active-switch" checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-xs text-muted-foreground">Active (usable at checkout)</span>
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <Button data-testid="promo-save-button" type="submit" disabled={saving} className="rounded-full bg-gold px-6 font-semibold text-ink hover:bg-gold-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form.id ? "Save Changes" : "Create Code"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setForm(null)} className="rounded-full border-gold/30 bg-transparent text-gold hover:bg-gold/10">Cancel</Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {promos.map((p) => (
          <div key={p.id} data-testid={`promo-row-${p.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-gold/15 bg-ink-card/60 p-4">
            <span className="rounded-lg border border-gold/25 bg-ink px-3 py-1.5 font-mono text-sm font-bold tracking-widest text-gold">{p.code}</span>
            <div className="min-w-0 flex-1">
              <p className="font-display font-semibold">
                {p.type === "percent" ? `${parseFloat(p.value)}% off` : `$${Number(p.value).toFixed(2)} off`}
              </p>
              <p className="text-xs text-muted-foreground">
                {p.applies_to === "all" ? "Everything" : p.applies_to === "products" ? "Merchandise only" : "Event tickets only"} · {p.active ? "Active" : "Disabled"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button data-testid={`promo-edit-${p.id}`} size="sm" variant="outline" onClick={() => setForm({ ...p, value: String(p.value) })} className="border-gold/30 bg-transparent text-gold hover:bg-gold/10"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button data-testid={`promo-delete-${p.id}`} size="sm" variant="outline" onClick={() => remove(p)} className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {promos.length === 0 && <p className="rounded-xl border border-dashed border-gold/20 py-8 text-center text-sm text-muted-foreground">No promo codes yet — create one for early-bird pricing or a merch sale.</p>}
      </div>
    </div>
  );
}

function DisplaySettings() {
  const [show, setShow] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/display").then(({ data }) => { setShow(data.show_spots); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const toggle = async (v) => {
    try {
      const { data } = await api.put("/admin/settings/display", { show_spots: v });
      setShow(data.show_spots);
      toast.success(data.show_spots ? "Remaining seats now show on upcoming events." : "Remaining seats hidden — sold-out still closes enrollment.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gold/15 bg-ink-card/70 p-5" data-testid="display-settings">
      <div className="max-w-xl">
        <p className="font-display font-semibold">Remaining seats countdown</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Shows "X of Y spots left" on upcoming events as they fill up. Sold-out events always close enrollment, even when the countdown is hidden.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">{show ? "Visible" : "Hidden"}</span>
        <Switch data-testid="show-spots-toggle" checked={show} onCheckedChange={toggle} disabled={!loaded} />
      </div>
    </div>
  );
}

function SeoSettings() {
  const [gaId, setGaId] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const sitemapUrl = `${window.location.origin}/api/sitemap.xml`;

  useEffect(() => {
    api.get("/admin/settings/analytics")
      .then(({ data }) => { setGaId(data.ga_id || ""); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/analytics", { ga_id: gaId });
      setGaId(data.ga_id);
      toast.success(data.ga_id ? `Analytics live — tracking starts within minutes under ${data.ga_id}.` : "Analytics disabled.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sitemapUrl);
      toast.success("Sitemap URL copied.");
    } catch {
      toast.error("Copy failed — long-press the URL to copy it.");
    }
  };

  return (
    <div className="space-y-5" data-testid="seo-settings">
      <div className="rounded-2xl border border-gold/15 bg-ink-card/70 p-5">
        <div className="flex items-start gap-3">
          <Globe className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
          <div className="min-w-0 flex-1">
            <p className="font-display font-semibold">Google Analytics (GA4)</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Paste your GA4 Measurement ID to start tracking visitors, traffic sources and page performance. Find it in Google Analytics under Admin → Data Streams → your stream (it looks like G-XXXXXXXXXX). Save empty to turn tracking off.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Input data-testid="ga-id-input" value={gaId} onChange={(e) => setGaId(e.target.value)} disabled={!loaded}
                placeholder="G-XXXXXXXXXX" className={`${inputCls} max-w-xs font-mono`} />
              <Button data-testid="ga-save-button" onClick={save} disabled={!loaded || saving}
                className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gold/15 bg-ink-card/70 p-5">
        <p className="font-display font-semibold">Your sitemap</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          The sitemap updates itself automatically whenever you add events, products or pages. Submit it once in Google Search Console (Sitemaps → Add a new sitemap) and Bing Webmaster Tools — after that, new content gets discovered on its own.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <code data-testid="sitemap-url" className="max-w-full truncate rounded-lg border border-gold/15 bg-ink px-3 py-2 font-mono text-xs text-gold">{sitemapUrl}</code>
          <Button data-testid="sitemap-copy-button" size="sm" variant="outline" onClick={copy}
            className="border-gold/30 bg-transparent text-gold hover:bg-gold/10">
            <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-gold/15 bg-ink-card/70 p-5">
        <p className="font-display font-semibold">Ongoing SEO checklist</p>
        <ul className="mt-2.5 space-y-2 text-xs leading-relaxed text-muted-foreground">
          <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" /> Every event, product and page form has an SEO description field with a Write-with-AI assistant — use it whenever you publish something new.</li>
          <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" /> Event and product pages automatically send structured data (Google rich results) with dates, prices and availability.</li>
          <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" /> Admin, checkout and staff pages are hidden from search engines automatically.</li>
          <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" /> robots.txt and llms.txt are live so search engines and AI assistants can read the site.</li>
        </ul>
      </div>
    </div>
  );
}

function DonationsManager() {
  const [donations, setDonations] = useState(null);
  const [wallEnabled, setWallEnabled] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/donations").then(({ data }) => setDonations(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/donations/settings").then(({ data }) => setWallEnabled(data.wall_enabled)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleWall = async (v) => {
    try {
      const { data } = await api.put("/admin/donations/settings", { wall_enabled: v });
      setWallEnabled(data.wall_enabled);
      toast.success(data.wall_enabled ? "Bridge-Builders wall is live on the Donate section." : "Bridge-Builders wall hidden from the site.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleWallEntry = async (d, show) => {
    try {
      await api.put(`/admin/donations/${d.id}/wall`, { show });
      toast.success(show ? `${(d.donor?.name || "Donor").split(" ")[0]} added to the wall.` : "Removed from the wall.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this monthly donation? The donor will not be charged again.")) return;
    try {
      await api.post(`/admin/donations/${id}/cancel`);
      toast.success("Recurring donation cancelled.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!donations) return <div className="flex py-10"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  const monthlyActive = donations.filter((d) => d.interval === "monthly" && d.status === "paid");
  const monthlyTotal = monthlyActive.reduce((s, d) => s + d.amount, 0);

  return (
    <div className="space-y-5" data-testid="donations-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gold/15 bg-ink-card/70 p-5" data-testid="wall-settings">
        <div className="max-w-xl">
          <p className="font-display font-semibold">Bridge-Builders wall</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Shows recent givers' first names on the Donate section. Donors opt in while giving — you can add or remove any name with the heart button below.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{wallEnabled ? "Live on site" : "Hidden"}</span>
          <Switch data-testid="wall-toggle" checked={wallEnabled} onCheckedChange={toggleWall} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-gold/15 bg-ink-card/70 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total donations</p>
          <p className="mt-2 font-display text-3xl font-semibold text-gold">{donations.filter((d) => d.status === "paid").length}</p>
        </div>
        <div className="rounded-2xl border border-gold/15 bg-ink-card/70 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Monthly givers</p>
          <p className="mt-2 font-display text-3xl font-semibold text-gold">{monthlyActive.length}</p>
        </div>
        <div className="rounded-2xl border border-gold/15 bg-ink-card/70 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Monthly recurring</p>
          <p className="mt-2 font-display text-3xl font-semibold text-gold">${monthlyTotal.toFixed(0)}/mo</p>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gold/15">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gold/15 bg-ink-card font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr><th className="px-4 py-3">Donor</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Collected</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {donations.map((d) => (
              <tr key={d.id} data-testid={`donation-row-${d.id}`} className="border-b border-gold/10 last:border-0 hover:bg-ink-card/50">
                <td className="px-4 py-3">
                  <p className="font-medium">{d.donor?.name || "—"}</p>
                  <p className="text-xs text-muted-foreground">{d.donor?.email || ""}</p>
                </td>
                <td className="px-4 py-3 text-gold">${Number(d.amount).toFixed(2)}{d.interval === "monthly" ? "/mo" : ""}</td>
                <td className="px-4 py-3 text-muted-foreground">{d.interval === "monthly" ? "Monthly" : "One-time"}</td>
                <td className="px-4 py-3 text-muted-foreground">${Number(d.total_collected || 0).toFixed(2)} ({d.payments_count || 0}×)</td>
                <td className="px-4 py-3"><StatusPill status={d.status} /></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    {d.interval === "monthly" && d.status === "paid" && (
                      <Button data-testid={`donation-cancel-${d.id}`} size="sm" variant="outline" onClick={() => cancel(d.id)}
                        className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10">Cancel</Button>
                    )}
                    {["paid", "active"].includes(d.status) && (
                      <Button data-testid={`donation-wall-${d.id}`} size="sm" variant="outline"
                        onClick={() => toggleWallEntry(d, !d.show_on_wall)}
                        title={d.show_on_wall ? "Remove from Bridge-Builders wall" : "Add to Bridge-Builders wall"}
                        className={d.show_on_wall ? "border-gold/50 bg-gold/15 text-gold hover:bg-gold/25" : "border-gold/20 bg-transparent text-muted-foreground hover:bg-gold/10"}>
                        <Heart className={`h-3.5 w-3.5 ${d.show_on_wall ? "fill-gold" : ""}`} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {donations.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-muted-foreground">No donations yet — share the Donate section!</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayoutSummary() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/payouts")
      .then(({ data }) => setData(data))
      .catch((e) => setError(formatApiError(e)));
  }, []);

  const sum = (list) => (list || []).reduce((s, b) => s + b.amount, 0);

  if (error) {
    return (
      <div className="max-w-3xl rounded-2xl border border-gold/20 bg-ink-card/70 p-6" data-testid="payout-summary">
        <p className="text-sm text-muted-foreground">Balance & payouts unavailable for the current account: <span className="text-red-300">{error}</span></p>
        <p className="mt-2 text-xs text-muted-foreground">Payouts typically appear once the Stripe account is fully activated and live charges have run.</p>
      </div>
    );
  }
  if (!data) return <div className="flex py-10"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="payout-summary">
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-gold/20 bg-ink-card/70 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Available balance</p>
          <p className="mt-2 font-display text-3xl font-semibold text-gold" data-testid="balance-available">${sum(data.available).toFixed(2)}</p>
          <p className="mt-1 text-xs text-muted-foreground">{(data.available[0] || {}).currency?.toUpperCase() || "USD"}</p>
        </div>
        <div className="rounded-2xl border border-gold/20 bg-ink-card/70 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pending</p>
          <p className="mt-2 font-display text-3xl font-semibold" data-testid="balance-pending">${sum(data.pending).toFixed(2)}</p>
          <p className="mt-1 text-xs text-muted-foreground">On its way to available</p>
        </div>
      </div>
      <div className="rounded-2xl border border-gold/20 bg-ink-card/70 p-6">
        <h3 className="font-display text-lg font-semibold">Recent payouts</h3>
        {data.payouts.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No payouts yet — they'll appear here once Stripe starts paying out to your bank.</p>
        ) : (
          <table className="mt-4 w-full text-left text-sm">
            <thead className="border-b border-gold/15 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <tr><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Arrival</th><th className="py-2">Initiated</th></tr>
            </thead>
            <tbody>
              {data.payouts.map((p) => (
                <tr key={p.id} data-testid={`payout-row-${p.id}`} className="border-b border-gold/10 last:border-0">
                  <td className="py-2.5 pr-4 font-semibold text-gold">${p.amount.toFixed(2)}</td>
                  <td className="py-2.5 pr-4"><StatusPill status={p.status} /></td>
                  <td className="py-2.5 pr-4 text-xs text-muted-foreground">{p.arrival_date ? new Date(p.arrival_date * 1000).toLocaleDateString() : "—"}</td>
                  <td className="py-2.5 text-xs text-muted-foreground">{new Date(p.created * 1000).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    confirmed: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
    paid: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
    pending_payment: "bg-amber-400/15 text-amber-300 border-amber-400/30",
    pending: "bg-amber-400/15 text-amber-300 border-amber-400/30",
    in_transit: "bg-sky-400/15 text-sky-300 border-sky-400/30",
    refunded: "bg-slate-400/15 text-slate-300 border-slate-400/30",
    voided: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
    canceled: "bg-red-400/15 text-red-300 border-red-400/30",
    failed: "bg-red-400/15 text-red-300 border-red-400/30",
    expired: "bg-slate-400/15 text-slate-300 border-slate-400/30",
  };
  return (
    <span className={`inline-block rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${styles[status] || styles.expired}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [forms, setForms] = useState([]);
  const [eventForm, setEventForm] = useState(null);
  const [productForm, setProductForm] = useState(null);
  const [formBuilder, setFormBuilder] = useState(null);
  const [subsForm, setSubsForm] = useState(null);
  const [ticketsReg, setTicketsReg] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, e, p, r, o, i, f] = await Promise.all([
        api.get("/admin/stats"), api.get("/events?all=true"), api.get("/products?all=true"),
        api.get("/admin/registrations"), api.get("/admin/orders"), api.get("/admin/inquiries"),
        api.get("/admin/forms"),
      ]);
      setStats(s.data); setEvents(e.data); setProducts(p.data);
      setRegistrations(r.data); setOrders(o.data); setInquiries(i.data); setForms(f.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => { setMe(data); loadAll(); })
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate, loadAll]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`).then(() => { toast.success("Event deleted."); loadAll(); }).catch((e) => toast.error(formatApiError(e)));
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`).then(() => { toast.success("Product deleted."); loadAll(); }).catch((e) => toast.error(formatApiError(e)));
  };

  const moveProduct = async (id, dir) => {
    const ids = products.map((p) => p.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setProducts(ids.map((pid) => products.find((p) => p.id === pid)));
    try {
      await api.put("/admin/products/order", { ids });
    } catch (err) {
      toast.error(formatApiError(err));
      loadAll();
    }
  };
  const sendReminders = async (id) => {
    try {
      const { data } = await api.post(`/admin/events/${id}/reminders`);
      toast.success(data.sent ? `${data.sent} reminder${data.sent > 1 ? "s" : ""} sent to confirmed attendees.` : "Everyone confirmed has already been reminded.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const refundOrder = async (id) => {
    if (!window.confirm("Refund this order in full? This cannot be undone.")) return;
    try {
      await api.post(`/admin/orders/${id}/refund`);
      toast.success("Order refunded.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const voidOrder = async (o) => {
    if (!window.confirm(`Void this order for ${o.customer?.name || o.customer?.email || "this customer"} ($${Number(o.amount).toFixed(2)})?\n\nIt stays on record as "voided", stops counting toward your totals, and any event spots/QR tickets it held are freed up. No money is moved — use Refund first if the buyer should be paid back.`)) return;
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/void`);
      toast.success(data.registrations_voided ? `Order voided — ${data.registrations_voided} registration${data.registrations_voided > 1 ? "s" : ""} voided too.` : "Order voided.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const deleteOrder = async (o) => {
    const paidWarning = o.status === "paid" ? `\n\nThis order was PAID $${Number(o.amount).toFixed(2)} — refund it first if the buyer should get their money back.` : "";
    if (!window.confirm(`Permanently DELETE this order for ${o.customer?.name || o.customer?.email || "this customer"} ($${Number(o.amount).toFixed(2)})?\n\nThe order and any event registrations/QR tickets tied to it are removed forever. This cannot be undone — best for test orders.${paidWarning}`)) return;
    try {
      const { data } = await api.delete(`/admin/orders/${o.id}`);
      toast.success(data.registrations_deleted ? `Order deleted — ${data.registrations_deleted} linked registration${data.registrations_deleted > 1 ? "s" : ""} removed too.` : "Order deleted.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const deleteForm = async (id) => {
    if (!window.confirm("Delete this form? Its submissions are kept, but the form will no longer be usable.")) return;
    await api.delete(`/admin/forms/${id}`).then(() => { toast.success("Form deleted."); loadAll(); }).catch((e) => toast.error(formatApiError(e)));
  };
  const deleteRegistration = async (r) => {
    const orderNote = r.order_id ? "\n\nThis registration is linked to an order — the order record stays in the Orders tab." : "";
    if (!window.confirm(`Permanently delete the registration for ${r.name} (${r.event_title || "event"})?\n\nTheir QR tickets stop working immediately and ${r.attendees || 1} spot${(r.attendees || 1) > 1 ? "s" : ""} free up. This cannot be undone — best for invalid or test registrations.${orderNote}`)) return;
    try {
      await api.delete(`/admin/registrations/${r.id}`);
      toast.success("Registration deleted — spot freed up.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const statCards = stats ? [
    { icon: Users, label: "Registrations", value: stats.registrations, sub: `${stats.confirmed_registrations} confirmed` },
    { icon: ShoppingBag, label: "Orders", value: stats.orders, sub: "all time" },
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue.toLocaleString()}`, sub: "paid orders + events" },
    { icon: CalendarDays, label: "Events", value: stats.events, sub: `${stats.products} products` },
  ] : [];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-foreground">
      <Seo noindex title="Admin Dashboard — AB Hunter Consulting USA" siteName="AB Hunter Consulting USA" description="Admin dashboard." />
      <header className="sticky top-0 z-40 border-b border-gold/15 bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="AB Hunter logo" className="h-10 w-auto object-contain" />
            <div>
              <p className="font-display text-sm font-semibold">Admin Dashboard</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{me?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site-link" className="text-xs text-muted-foreground transition-colors hover:text-gold">View site</a>
            <Button data-testid="admin-logout-button" variant="outline" size="sm" onClick={logout}
              className="rounded-full border-gold/30 bg-transparent text-gold hover:bg-gold/10">
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats-row">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="rounded-2xl border border-gold/15 bg-ink-card/70 p-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.label}</span>
                  <Icon className="h-4 w-4 text-gold" />
                </div>
                <p className="mt-3 font-display text-3xl font-semibold text-gold">{s.value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{s.sub}</p>
              </div>
            );
          })}
        </div>

        <Tabs defaultValue="events" className="mt-10">
          <TabsList data-testid="admin-tabs" className="flex-wrap border border-gold/15 bg-ink-card">
            <TabsTrigger data-testid="admin-tab-events" value="events">Events</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-products" value="products">Products</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-promos" value="promos">Promos</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-forms" value="forms">Forms</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-registrations" value="registrations">Registrations</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-orders" value="orders">Orders</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-inquiries" value="inquiries">Inquiries</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-donations" value="donations">Donations</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-content" value="content">Content</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-blog" value="blog">Blog</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-ai" value="ai">AI Editor</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-settings" value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold">Events ({events.length})</h2>
              <Button data-testid="add-event-button" onClick={() => setEventForm({ ...EMPTY_EVENT })} className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
                <Plus className="mr-1.5 h-4 w-4" /> Add Event
              </Button>
            </div>
            {(() => {
              const today = new Date().toISOString().slice(0, 10);
              const upcoming = events
                .filter((e) => !e.start_date || e.start_date >= today)
                .sort((a, b) => (a.start_date || "9999").localeCompare(b.start_date || "9999"));
              const past = events
                .filter((e) => e.start_date && e.start_date < today)
                .sort((a, b) => b.start_date.localeCompare(a.start_date));
              const Row = (e) => (
                <div key={e.id} data-testid={`admin-event-row-${e.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-gold/15 bg-ink-card/60 p-4">
                  <img src={e.image || "/assets/academy-1.png"} alt="" className="h-14 w-14 rounded-lg border border-gold/20 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold">{e.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {e.date_label} · {Number(e.price) > 0 ? `$${Number(e.price).toFixed(0)}${e.ticketed ? " · checkout" : ""}` : "Free"} · {e.active ? "Visible" : "Hidden"}
                      {Number(e.price) <= 0 && e.form_id ? ` · Form: ${(forms.find((f) => f.id === e.form_id) || {}).name || e.form_id}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`admin-event-remind-${e.id}`} size="sm" variant="outline" onClick={() => sendReminders(e.id)}
                      title="Email a reminder to confirmed attendees"
                      className="border-gold/30 bg-transparent text-gold hover:bg-gold/10"><Bell className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`admin-event-edit-${e.id}`} size="sm" variant="outline" onClick={() => setEventForm(e)} className="border-gold/30 bg-transparent text-gold hover:bg-gold/10"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`admin-event-delete-${e.id}`} size="sm" variant="outline" onClick={() => deleteEvent(e.id)} className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
              return (
                <>
                  <h3 className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-gold" data-testid="upcoming-events-heading">Upcoming ({upcoming.length})</h3>
                  <div className="space-y-3" data-testid="upcoming-events-list">
                    {upcoming.map(Row)}
                    {upcoming.length === 0 && <p className="rounded-xl border border-dashed border-gold/20 py-8 text-center text-sm text-muted-foreground">No upcoming events. Add your first one.</p>}
                  </div>
                  <h3 className="mb-3 mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground" data-testid="past-events-heading">Past Events ({past.length})</h3>
                  <div className="space-y-3 opacity-70" data-testid="past-events-list">
                    {past.map(Row)}
                    {past.length === 0 && <p className="rounded-xl border border-dashed border-gold/20 py-8 text-center text-sm text-muted-foreground">No past events yet — they'll land here automatically once their date passes.</p>}
                  </div>
                </>
              );
            })()}
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl font-semibold">Products ({products.length})</h2>
                <p className="mt-1 text-xs text-muted-foreground">Use the arrows to set the order merch appears in the shop.</p>
              </div>
              <Button data-testid="add-product-button" onClick={() => setProductForm({ ...EMPTY_PRODUCT })} className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
                <Plus className="mr-1.5 h-4 w-4" /> Add Product
              </Button>
            </div>
            <div className="space-y-3">
              {products.map((p, idx) => (
                <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-gold/15 bg-ink-card/60 p-4">
                  <div className="flex flex-col gap-1">
                    <button data-testid={`admin-product-up-${p.id}`} aria-label={`Move ${p.name} up`} disabled={idx === 0} onClick={() => moveProduct(p.id, -1)}
                      className="flex h-6 w-6 items-center justify-center rounded-md border border-gold/20 text-gold transition-colors hover:bg-gold/10 disabled:opacity-25 disabled:hover:bg-transparent">
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`admin-product-down-${p.id}`} aria-label={`Move ${p.name} down`} disabled={idx === products.length - 1} onClick={() => moveProduct(p.id, 1)}
                      className="flex h-6 w-6 items-center justify-center rounded-md border border-gold/20 text-gold transition-colors hover:bg-gold/10 disabled:opacity-25 disabled:hover:bg-transparent">
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <img src={p.image || "/assets/product-book.png"} alt="" className="h-14 w-14 rounded-lg border border-gold/20 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground">${Number(p.price).toFixed(2)} · {p.category} · {p.active ? "Visible" : "Hidden"}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`admin-product-edit-${p.id}`} size="sm" variant="outline" onClick={() => setProductForm(p)} className="border-gold/30 bg-transparent text-gold hover:bg-gold/10"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`admin-product-delete-${p.id}`} size="sm" variant="outline" onClick={() => deleteProduct(p.id)} className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
              {products.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No products yet. Add your first one.</p>}
            </div>
          </TabsContent>

          <TabsContent value="promos" className="mt-6">
            <h2 className="mb-5 font-display text-xl font-semibold">Promo Codes</h2>
            <PromosManager />
          </TabsContent>

          <TabsContent value="forms" className="mt-6">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl font-semibold">Forms ({forms.length})</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Contact forms show on the site & thank-you page. Registration forms attach to free events as their signup.
                </p>
              </div>
              <Button data-testid="add-form-button" onClick={() => setFormBuilder({ ...EMPTY_FORM, fields: [] })} className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">
                <Plus className="mr-1.5 h-4 w-4" /> New Form
              </Button>
            </div>
            <div className="space-y-3">
              {forms.map((f) => (
                <div key={f.id} data-testid={`admin-form-row-${f.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-gold/15 bg-ink-card/60 p-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/20 bg-ink text-gold">
                    <ClipboardList className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold">{f.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(f.fields || []).length} fields · {f.purpose.replace(/_/g, " ")} · {f.active ? "Active" : "Inactive"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`admin-form-submissions-${f.id}`} size="sm" variant="outline" onClick={() => setSubsForm(f)} className="border-gold/30 bg-transparent text-gold hover:bg-gold/10">
                      <Eye className="mr-1 h-3.5 w-3.5" /> Submissions
                    </Button>
                    <Button data-testid={`admin-form-edit-${f.id}`} size="sm" variant="outline" onClick={() => setFormBuilder(f)} className="border-gold/30 bg-transparent text-gold hover:bg-gold/10"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`admin-form-delete-${f.id}`} size="sm" variant="outline" onClick={() => deleteForm(f.id)} className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
              {forms.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No forms yet. Create your first one.</p>}
            </div>
          </TabsContent>

          <TabsContent value="registrations" className="mt-6">
            <h2 className="mb-5 font-display text-xl font-semibold">Event Registrations ({registrations.length})</h2>
            <div className="overflow-x-auto rounded-xl border border-gold/15" data-testid="registrations-table">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gold/15 bg-ink-card font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Event</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Attendees</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Tickets</th></tr>
                </thead>
                <tbody>
                  {registrations.map((r) => (
                    <tr key={r.id} data-testid={`registration-row-${r.id}`} className="border-b border-gold/10 last:border-0 hover:bg-ink-card/50">
                      <td className="px-4 py-3 font-medium">{r.name}
                        {r.attendee_names?.length > 1 && (
                          <p className="mt-0.5 text-xs font-normal text-muted-foreground">+ {r.attendee_names.slice(1).join(", ")}</p>
                        )}
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-muted-foreground">{r.event_title}{r.tier ? <span className="block truncate text-gold/70">{r.tier}</span> : null}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
                      <td className="px-4 py-3">{r.attendees}</td>
                      <td className="px-4 py-3 text-gold">${Number(r.amount).toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <Button data-testid={`registration-tickets-${r.id}`} size="sm" variant="outline" onClick={() => setTicketsReg(r)}
                            className="border-gold/30 bg-transparent text-gold hover:bg-gold/10">
                            <QrCode className="mr-1 h-3.5 w-3.5" /> Tickets
                          </Button>
                          <Button data-testid={`registration-delete-${r.id}`} size="sm" variant="outline" onClick={() => deleteRegistration(r)} aria-label="Delete registration"
                            className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {registrations.length === 0 && <tr><td colSpan="8" className="px-4 py-10 text-center text-muted-foreground">No registrations yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <h2 className="mb-5 font-display text-xl font-semibold">Product Orders ({orders.length})</h2>
            <div className="overflow-x-auto rounded-xl border border-gold/15" data-testid="orders-table">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gold/15 bg-ink-card font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Actions</th></tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-gold/10 last:border-0 hover:bg-ink-card/50">
                      <td className="px-4 py-3">
                        <p className="font-medium">{o.customer?.name || "—"}</p>
                        <p className="text-xs text-muted-foreground">{o.customer?.email || ""}</p>
                        {o.shipping && (
                          <p className="mt-0.5 text-xs text-gold/80" data-testid={`order-shipping-${o.id}`}>
                            Ship to: {o.shipping.address1}{o.shipping.address2 ? `, ${o.shipping.address2}` : ""}, {o.shipping.city}, {o.shipping.state} {o.shipping.zip}
                          </p>
                        )}
                        {o.promo_code && (
                          <p className="mt-0.5 text-xs text-emerald-300" data-testid={`order-promo-${o.id}`}>Promo {o.promo_code} (−${Number(o.discount || 0).toFixed(2)})</p>
                        )}
                      </td>
                      <td className="max-w-[260px] px-4 py-3">
                        <p className="truncate text-muted-foreground">
                          {(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ") || o.product_name || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gold">${Number(o.amount).toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {o.status === "paid" && o.payment_intent_id && (
                            <Button data-testid={`order-refund-${o.id}`} size="sm" variant="outline" onClick={() => refundOrder(o.id)}
                              className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10">Refund</Button>
                          )}
                          {o.status !== "voided" && o.status !== "refunded" && (
                            <Button data-testid={`order-void-${o.id}`} size="sm" variant="outline" onClick={() => voidOrder(o)}
                              className="border-amber-500/30 bg-transparent text-amber-300 hover:bg-amber-500/10">Void</Button>
                          )}
                          <Button data-testid={`order-delete-${o.id}`} size="sm" variant="outline" onClick={() => deleteOrder(o)}
                            className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-muted-foreground">No orders yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <h2 className="mb-5 font-display text-xl font-semibold">Consultation Inquiries ({inquiries.length})</h2>
            <div className="space-y-3" data-testid="inquiries-list">
              {inquiries.map((q) => (
                <div key={q.id} data-testid={`inquiry-row-${q.id}`} className="rounded-xl border border-gold/15 bg-ink-card/60 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-display font-semibold">{q.name} <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{q.service}</span></p>
                    <p className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</p>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground"><Mail className="mr-1 inline h-3 w-3" />{q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
                  <p className="mt-3 text-sm text-foreground/80">{q.message}</p>
                </div>
              ))}
              {inquiries.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No inquiries yet. New contact-form messages appear under Forms → Submissions.</p>}
            </div>
          </TabsContent>
          <TabsContent value="donations" className="mt-6">
            <h2 className="mb-5 font-display text-xl font-semibold">Giving Campaigns</h2>
            <CampaignsManager />
            <h2 className="mb-5 mt-12 font-display text-xl font-semibold">Donations</h2>
            <DonationsManager />
          </TabsContent>

          <TabsContent value="content" className="mt-6">
            <h2 className="mb-2 font-display text-xl font-semibold">Site Content</h2>
            <p className="mb-5 max-w-2xl text-xs text-muted-foreground">Edit any text below and publish — changes go live on the site instantly.</p>
            <ContentManager />
            <h2 className="mb-5 mt-12 font-display text-xl font-semibold">Custom Pages</h2>
            <PagesManager />
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            <h2 className="mb-2 font-display text-xl font-semibold">Blog</h2>
            <p className="mb-5 max-w-2xl text-xs text-muted-foreground">Write and publish posts — they appear at /blog with their own pages, SEO descriptions and social share cards. Drafts stay hidden until you publish.</p>
            <BlogManager />
          </TabsContent>

          <TabsContent value="ai" className="mt-6">
            <h2 className="mb-2 font-display text-xl font-semibold">AI Site Editor</h2>
            <p className="mb-5 max-w-2xl text-xs text-muted-foreground">Describe a change in plain words — the AI manages the site's content, pages, events, products and blog posts, including editing existing ones. Design or code changes go through your Sitelytic developer chat.</p>
            <AiEditor />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <h2 className="mb-5 font-display text-xl font-semibold">Payment Settings</h2>
            <PaymentSettings />
            <h2 className="mb-5 mt-12 font-display text-xl font-semibold">Event Display</h2>
            <DisplaySettings />
            <h2 className="mb-5 mt-12 font-display text-xl font-semibold">SEO & Analytics</h2>
            <SeoSettings />
            <h2 className="mb-5 mt-12 font-display text-xl font-semibold">Check-In Staff</h2>
            <StaffManager />
            <h2 className="mb-5 mt-12 font-display text-xl font-semibold">Balance & Payouts</h2>
            <PayoutSummary />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!eventForm} onOpenChange={(o) => !o && setEventForm(null)}>
        <DialogContent data-testid="event-form-dialog" className="max-h-[90vh] overflow-y-auto border-gold/25 bg-ink-card text-foreground sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{eventForm?.id ? "Edit Event" : "Add New Event"}</DialogTitle></DialogHeader>
          {eventForm && <EventForm initial={eventForm} onSave={loadAll} onClose={() => setEventForm(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!productForm} onOpenChange={(o) => !o && setProductForm(null)}>
        <DialogContent data-testid="product-form-dialog" className="max-h-[90vh] overflow-y-auto border-gold/25 bg-ink-card text-foreground sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{productForm?.id ? "Edit Product" : "Add New Product"}</DialogTitle></DialogHeader>
          {productForm && <ProductForm initial={productForm} onSave={loadAll} onClose={() => setProductForm(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!formBuilder} onOpenChange={(o) => !o && setFormBuilder(null)}>
        <DialogContent data-testid="form-builder-dialog" className="max-h-[90vh] overflow-y-auto border-gold/25 bg-ink-card text-foreground sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{formBuilder?.id ? `Edit Form — ${formBuilder.name}` : "Create New Form"}</DialogTitle></DialogHeader>
          {formBuilder && <FormBuilder initial={formBuilder} onSave={loadAll} onClose={() => setFormBuilder(null)} />}
        </DialogContent>
      </Dialog>

      <SubmissionsDialog form={subsForm} onClose={() => setSubsForm(null)} />
      <TicketsDialog reg={ticketsReg} onClose={() => setTicketsReg(null)} />
    </div>
  );
}
